import { Avatar, Box, Grid } from "@mui/material";
import Layout from "../../components/Layout/Layout";
import QueryPageTitleCard from "../../components/QueryPageTitleCard/QueryPageTitleCard";
import CollapsibleFilterCard from "../../components/CollapsibleFilterCard/CollapsibleFilterCard";
import CustomCard from "../../components/CustomCard/CustomCard";
import { routesConfig } from "../../config/routes";
import { getFormattedTimestamp } from "../../utils/dateFormatter";
import { useNavigate } from "react-router-dom";
import React, { useMemo, useState } from "react";
import { AuthenticationContextData } from "../../contexts/authentication/domain";
import { useAuth } from "../../contexts/authentication/AuthenticationContext";
import { Roles } from "../../components/SideBar/Content/ListItem";
import { TypesPermissions } from "../../components/Routes/TypesPermissions";
import { useFlags } from "flagsmith/react";
import { sidebar } from "../../utils/featureFlags";
import CustomButton, {
  CustomButtonVariant,
} from "../../components/CustomButton/CustomButton";
import CustomTabs from "../../components/CustomTabs/CustomTabs";
import MultipleSelectDistributors from "../../components/MultipleSelectDistributors/MultipleSelectDistributors";
import SelectSubsidiaries from "../../components/SelectSubsidiary/SelectSubsidiary";
import { useDistributor } from "../../contexts/distributor/DistributorContext";
import CalendarRange from "../../components/CalendarRange/CalendarRange";
import { getFirstDayOfCurrentMonth } from "../../utils";
import RiverDeliveriesTab from "./Sections/RiverDeliveriesTab";
import {
  openedInitQuery,
  clearedInitQuery,
  rejectedInitQuery,
} from "./models/OptionsQuery";
import {
  NO_OPEN_ORDERS_TITLE,
  NO_CLEARED_ORDERS_TITLE,
  NO_REJECTED_ORDERS_TITLE,
} from "../../utils/titles";

const RiverDeliveriesPage: React.FC = () => {
  const navigate = useNavigate();
  const flags = useFlags(sidebar);
  const currentDay = new Date();
  const { authByPermission, permissionSet, roles } =
    useAuth() as AuthenticationContextData;

  const { selectedDistributorsStoraged, subsidiaries } = useDistributor();
  const [openedCount, setOpenedCount] = useState(0);
  const [clearedCount, setClearedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [codeSubsidiary, setCodeSubsidiary] = useState<string | undefined>(
    undefined
  );
  const [dateFilterFrom, setDateFilterFrom] = useState<Date | null>(null);
  const [dateFilterTo, setDateFilterTo] = useState<Date | null>(null);

  const [clearValueCalendar, setClearValueCalendar] = useState<boolean>(false);

  const clearFilters = () => {
    setClearValueCalendar(true);
    setCodeSubsidiary(undefined);
    setDateFilterFrom(null);
    setDateFilterTo(null);
  };

  const selectedDistributors = useMemo(() => {
    return selectedDistributorsStoraged.join(",");
  }, [selectedDistributorsStoraged]);

  const isAllowed = useMemo(() => {
    return (
      authByPermission(
        permissionSet.SYSTEM_MODULES,
        roles.has(Roles.ADMIN),
        TypesPermissions.RIVER_REQUEST
      ) && flags.operational_new_river.enabled
    );
  }, [authByPermission, flags]);

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{
              width: 60,
              height: 60,
            }}
          />
        ),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <QueryPageTitleCard
                title="Entregas fluviais"
                componentItem={
                  <CustomButton
                    title="Novo pedido Fluvial"
                    onClick={() => {
                      navigate(routesConfig.OPERATIONAL().RIVER_REQUEST);
                    }}
                    variant={
                      isAllowed
                        ? CustomButtonVariant.CONTAINED
                        : CustomButtonVariant.DISABLED
                    }
                    disabled={!isAllowed}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CollapsibleFilterCard
                title="Pedidos de agendamento"
                subtitle={getFormattedTimestamp(new Date())}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <MultipleSelectDistributors
                      maxWidth={1200}
                      label={"Selecione o cliente"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <SelectSubsidiaries
                      label="Filial"
                      valueSubsidiary={subsidiaries.find(
                        (item) => item.id === codeSubsidiary
                      )}
                      onChangeValue={(selectedOption) => {
                        if (selectedOption) {
                          setCodeSubsidiary(selectedOption.id);
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <CalendarRange
                      name="date"
                      label="Data de agendamento"
                      clearValueCalendar={clearValueCalendar}
                      firstDayOfMonth={getFirstDayOfCurrentMonth()}
                      lastDayOfMonth={currentDay}
                      onChangeDateStartEnd={(dateBegin, dateEnd) => {
                        setClearValueCalendar(false);
                        // eslint-disable-next-line
                        if (dateBegin && dateEnd) {
                          setDateFilterFrom(dateBegin);
                          setDateFilterTo(dateEnd);
                        }
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    justifyContent="flex-end"
                    gap={1}
                  >
                    <CustomButton
                      title="Limpar filtros"
                      variant={CustomButtonVariant.OUTLINED}
                      onClick={clearFilters}
                    />
                  </Grid>
                </Grid>
              </CollapsibleFilterCard>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomCard
            sx={{
              boxShadow: "none",
              border: "1px solid #DFE0EB",
              padding: 2.4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <CustomTabs
                  tabsHeader={[
                    `Em aberto (${openedCount})`,
                    `Faturado (${clearedCount})`,
                    `Reprovado (${rejectedCount})`,
                  ]}
                  body={[
                    <RiverDeliveriesTab
                      key={0}
                      initQuery={openedInitQuery}
                      emptyTitle={NO_OPEN_ORDERS_TITLE}
                      emptySubtitle='Para pedir clique no botão "Novo pedido fluvial"'
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setOpenedCount(count);
                      }}
                    />,
                    <RiverDeliveriesTab
                      key={1}
                      initQuery={clearedInitQuery}
                      emptyTitle={NO_CLEARED_ORDERS_TITLE}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setClearedCount(count);
                      }}
                    />,
                    <RiverDeliveriesTab
                      key={2}
                      initQuery={rejectedInitQuery}
                      emptyTitle={NO_REJECTED_ORDERS_TITLE}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setRejectedCount(count);
                      }}
                    />,
                  ]}
                />
              </Grid>
            </Grid>
          </CustomCard>
        </Box>
      </Box>
    </Layout>
  );
};

export default RiverDeliveriesPage;
